import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Paper, 
  FormControl, 
  FormControlLabel, 
  RadioGroup, 
  Radio,
  Snackbar,
  Alert
} from '@mui/material';
import { registerUser } from './api';

const Registration = ({ onRegistrationSuccess }) => {
  const [registrationType, setRegistrationType] = useState('email');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const handleRegistrationTypeChange = (event) => {
    setRegistrationType(event.target.value);
    // Clear previously entered data when switching type
    setEmail('');
    setPhone('');
  };

  const handleRegister = async (e) => {
    e.preventDefault();
  
    if (!name) {
      setError('Name is required');
      return;
    }
    if (registrationType === 'email' && !email) {
      setError('Email is required');
      return;
    }
    if (registrationType === 'phone' && !phone) {
      setError('Phone number is required');
      return;
    }
  
    try {
      setLoading(true);
      setError(null);
  
      const userData = { 
        name,
        shopId: '' // Overridden by backend
      };
      
      if (registrationType === 'email') {
        userData.email = email;
      } else {
        userData.phone = phone;
      }
  
      const response = await registerUser(userData);
      console.log('User created:', response);
  
      // ✅ Send email and phone explicitly
      onRegistrationSuccess(response.email, response.phone);
  
      setSuccess(true);
      setName('');
      setEmail('');
      setPhone('');
      setShowForm(false);
    } catch (err) {
      console.error('Registration error:', err);
      let errorMessage = 'Registration failed. Please try again.';
  
      if (err.response && err.response.data && err.response.data.detail) {
        const detail = err.response.data.detail;
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (Array.isArray(detail)) {
          errorMessage = detail.map(item => item.msg).join(', ');
        } else if (typeof detail === 'object') {
          errorMessage = detail.msg || JSON.stringify(detail);
        }
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };
  

  return (
    <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
      <Typography variant="h5" component="h2" gutterBottom>
        User Registration
      </Typography>
      
      {!showForm ? (
        <Button 
          variant="contained" 
          color="primary" 
          onClick={() => setShowForm(true)}
          fullWidth
        >
          Register to Claim Rewards
        </Button>
      ) : (
        <Box component="form" onSubmit={handleRegister} noValidate>
          <FormControl component="fieldset" sx={{ mb: 2 }}>
            <Typography variant="subtitle1">Register with:</Typography>
            <RadioGroup
              row
              name="registration-type"
              value={registrationType}
              onChange={handleRegistrationTypeChange}
            >
              <FormControlLabel value="email" control={<Radio />} label="Email" />
              <FormControlLabel value="phone" control={<Radio />} label="Phone" />
            </RadioGroup>
          </FormControl>
          
          <TextField
            margin="normal"
            required
            fullWidth
            id="name"
            label="Full Name"
            name="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
          />
          
          {registrationType === 'email' ? (
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          ) : (
            <TextField
              margin="normal"
              required
              fullWidth
              id="phone"
              label="Phone Number"
              name="phone"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+1234567890"
            />
          )}
          
          <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading}
              sx={{ flex: 1 }}
            >
              {loading ? 'Registering...' : 'Register'}
            </Button>
            <Button
              variant="outlined"
              onClick={() => setShowForm(false)}
              sx={{ flex: 1 }}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      )}
      
      <Snackbar 
        open={error !== null} 
        autoHideDuration={6000} 
        onClose={() => setError(null)}
      >
        <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
      
      <Snackbar 
        open={success} 
        autoHideDuration={6000} 
        onClose={() => setSuccess(false)}
      >
        <Alert onClose={() => setSuccess(false)} severity="success" sx={{ width: '100%' }}>
          Registration successful!
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default Registration;
